import * as Headless from '@headlessui/react'
import clsx from 'clsx'
import React, { forwardRef } from 'react'
import { Link } from './link'

const styles = {
  base: [
    'relative isolate inline-flex items-center justify-center gap-x-2 rounded-lg border text-base/6 font-semibold',
    'px-3.5 py-2.5 sm:px-3 sm:py-1.5 sm:text-sm/6',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500',
    'data-[disabled]:opacity-50',
    '*:data-[slot=icon]:-mx-0.5 *:data-[slot=icon]:my-0.5 *:data-[slot=icon]:size-5 *:data-[slot=icon]:shrink-0 *:data-[slot=icon]:self-center *:data-[slot=icon]:text-[var(--btn-icon)] sm:*:data-[slot=icon]:my-1 sm:*:data-[slot=icon]:size-4',
  ],

  solid: [
    'border-transparent bg-[var(--btn-border)]',
    'before:absolute before:inset-0 before:-z-10 before:rounded-[calc(var(--radius-lg,0.5rem)-1px)] before:bg-[var(--btn-bg)]',
    'before:shadow-sm',
    'dark:bg-[var(--btn-bg)] dark:before:hidden dark:border-white/5',
    'after:absolute after:inset-0 after:-z-10 after:rounded-[calc(var(--radius-lg,0.5rem)-1px)]',
    'after:shadow-[inset_0_1px_rgba(255,255,255,0.15)]',
    'hover:after:bg-[var(--btn-hover-overlay)] data-[active]:after:bg-[var(--btn-hover-overlay)]',
    'dark:after:-inset-px dark:after:rounded-lg',
    'data-[disabled]:before:shadow-none data-[disabled]:after:shadow-none',
  ],

  outline: [
    'border-zinc-950/10 text-zinc-950 hover:bg-zinc-950/5 data-[active]:bg-zinc-950/5',
    'dark:border-white/15 dark:text-white dark:[--btn-bg:transparent] dark:hover:bg-white/5 dark:data-[active]:bg-white/5',
    '[--btn-icon:rgb(113_113_122)] hover:[--btn-icon:rgb(63_63_70)] data-[active]:[--btn-icon:rgb(63_63_70)] dark:[--btn-icon:rgb(161_161_170)]',
  ],

  plain: [
    'border-transparent text-zinc-950 hover:bg-zinc-950/5 data-[active]:bg-zinc-950/5',
    'dark:text-white dark:hover:bg-white/10 dark:data-[active]:bg-white/10',
    '[--btn-icon:rgb(113_113_122)] hover:[--btn-icon:rgb(63_63_70)] data-[active]:[--btn-icon:rgb(63_63_70)] dark:[--btn-icon:rgb(161_161_170)]',
  ],

  colors: {
    'dark/zinc': [
      'text-white [--btn-bg:rgb(24_24_27)] [--btn-border:rgb(9_9_11)]/90 [--btn-hover-overlay:rgba(255,255,255,0.10)]',
      'dark:text-white dark:[--btn-bg:rgb(82_82_91)] dark:[--btn-hover-overlay:rgba(255,255,255,0.05)]',
      '[--btn-icon:rgb(161_161_170)]',
    ],
    light: [
      'text-zinc-950 [--btn-bg:white] [--btn-border:rgb(9_9_11)]/10 [--btn-hover-overlay:rgba(9,9,11,0.025)]',
      'dark:text-white dark:[--btn-bg:rgb(39_39_42)]',
      '[--btn-icon:rgb(113_113_122)]',
    ],
    brand: [
      'text-white [--btn-hover-overlay:rgba(255,255,255,0.10)] [--btn-bg:rgb(var(--color-brand-600))] [--btn-border:rgb(var(--color-brand-700))]',
      '[--btn-icon:rgb(var(--color-brand-300))]',
    ],
    darktheme: [
      'inline-flex items-center gap-2 rounded-xl text-sm font-medium',
      '!cursor-pointer border !border-dark-border-button !bg-dark-background-secondary-medium !px-3 !py-2',
      'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-dark-background-secondary-medium',
      '!text-dark-text hover:bg-slate-50 before:hidden after:hidden',
    ],
    soft: [
      '!font-normal text-[rgba(74,85,101,1)] [--btn-bg:rgba(249,250,251,1)] [--btn-border:rgba(229,231,235,1)] [--btn-hover-overlay:rgba(0,0,0,0.02)]',
      'hover:[--btn-bg:rgba(243,244,246,1)] [--btn-icon:rgb(113_113_122)]',
    ],

    // ✅ New Gradient Style
    gradient: [
      'text-white border-0 !shadow-md relative overflow-hidden',
      'before:absolute before:inset-0 before:rounded-lg before:z-[-1]',
      'before:bg-[linear-gradient(94.86deg,#372AAC_0.95%,#8A38F5_110.32%)]',
      'hover:before:brightness-110 transition-all duration-200',
      'disabled:opacity-60 disabled:cursor-not-allowed',
    ],
  },
}

type ButtonProps =
  | ({ color?: keyof typeof styles.colors; outline?: never; plain?: never } & { className?: string; children: React.ReactNode } & Omit<Headless.ButtonProps, 'as' | 'className'>)
  | ({ color?: never; outline: true; plain?: never } & { className?: string; children: React.ReactNode } & Omit<Headless.ButtonProps, 'as' | 'className'>)
  | ({ color?: never; outline?: never; plain: true } & { className?: string; children: React.ReactNode } & Omit<Headless.ButtonProps, 'as' | 'className'>)
  | ({ color?: keyof typeof styles.colors; outline?: never; plain?: never } & { className?: string; children: React.ReactNode } & Omit<React.ComponentPropsWithoutRef<typeof Link>, 'className'>)
  | ({ color?: never; outline: true; plain?: never } & { className?: string; children: React.ReactNode } & Omit<React.ComponentPropsWithoutRef<typeof Link>, 'className'>)
  | ({ color?: never; outline?: never; plain: true } & { className?: string; children: React.ReactNode } & Omit<React.ComponentPropsWithoutRef<typeof Link>, 'className'>)

export const Button = forwardRef(function Button(
  { color, outline, plain, className, children, ...props }: ButtonProps,
  ref: React.ForwardedRef<HTMLElement>
) {
  const classes = clsx(
    styles.base,
    outline
      ? styles.outline
      : plain
      ? styles.plain
      : clsx(styles.solid, styles.colors[color ?? 'dark/zinc']),
    className
  )

  return 'href' in props ? (
    <Link {...(props as any)} className={classes} ref={ref as React.ForwardedRef<HTMLAnchorElement>}>
      <TouchTarget>{children}</TouchTarget>
    </Link>
  ) : (
    <Headless.Button {...(props as any)} className={classes} ref={ref as React.ForwardedRef<HTMLButtonElement>}>
      <TouchTarget>{children}</TouchTarget>
    </Headless.Button>
  )
})

export function TouchTarget({ children }: { children: React.ReactNode }) {
  return (
    <>
      <span
        className="absolute left-1/2 top-1/2 size-[max(100%,2.75rem)] -translate-x-1/2 -translate-y-1/2 pointer-fine:hidden"
        aria-hidden="true"
      />
      {children}
    </>
  )
}
